import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState, useMemo } from 'react';
import useFetch from 'use-http';
import { BiX, BiSearch } from 'react-icons/bi';
import { Loader2 } from 'src/components/common/icons';
import * as styles from './styles'; // Using your consistent styles
import { IInvoiceSearchResult, modalStyles } from './CreateInvoiceGroupModal';

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    groupNo: number | null;
}

const GroupLeasesModal: React.FC<IProps> = ({ isOpen, onClose, groupNo }) => {
    const { get, response, loading } = useFetch();
    const [allLeases, setAllLeases] = useState<IInvoiceSearchResult[]>([]);

    // Local Search State
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (isOpen && groupNo) {
            const loadData = async () => {
                const data = await get(`/v1.0/leases/invoice-groups/${groupNo}/leases`);
                if (response.ok) {
                    setAllLeases(data);
                }
            };
            loadData();
        } else {
            setAllLeases([]);
            setSearchTerm('');
        }
    }, [isOpen, groupNo, get, response]);

    // --- Computed Data ---

    // 1. Filtered List (Local Search)
    const filteredLeases = useMemo(() => {
        if (!searchTerm) {
            return allLeases;
        }

        const lowerTerm = searchTerm.toLowerCase();
        return allLeases.filter(
            (lease) =>
                lease.lsNo.toString().includes(lowerTerm) ||
                (lease.invoiceNo && lease.invoiceNo.toString().toLowerCase().includes(lowerTerm)) ||
                (lease.lessor && lease.lessor.toLowerCase().includes(lowerTerm))
        );
    }, [allLeases, searchTerm]);

    // 2. Total Calculation (Based on Visible Rows)
    const totalAmount = useMemo(() => {
        return filteredLeases.reduce((sum, r) => sum + (r.invoiceTotal || 0), 0);
    }, [filteredLeases]);

    // --- Handlers ---

    const formatMoney = (val?: number) => (val ? val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');
    const formatDate = (dateStr: string) => (dateStr ? new Date(dateStr).toLocaleDateString() : '');

    if (!isOpen) {
        return null;
    }

    return (
        <div style={modalStyles.modalOverlay}>
            <div style={{ ...modalStyles.modalContent, width: '1300px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <div style={modalStyles.modalHeader}>
                    <h3 style={modalStyles.modalTitle}>Attached Leases for Invoice Group {groupNo}</h3>
                    <button onClick={onClose} style={modalStyles.iconButton}>
                        <BiX size={24} />
                    </button>
                </div>

                {/* Local Filter Bar */}
                <div style={{ padding: '1rem', borderBottom: `1px solid ${styles.colors.border}`, backgroundColor: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', maxWidth: '400px' }}>
                        <BiSearch size={20} color={styles.colors.gray500} />
                        <LeaseRecordInput
                            style={{ ...localInput, padding: '0.5rem' }}
                            placeholder="Filter by Lease #, Invoice #, or Lessor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus={true}
                        />
                    </div>
                </div>

                {/* Grid */}
                <div style={{ flex: 1, overflow: 'auto', position: 'relative', backgroundColor: '#fff' }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                            <Loader2 style={{ animation: 'spin 1s linear infinite', width: 32, height: 32 }} />
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: '1200px' }}>
                            <thead
                                style={{
                                    position: 'sticky',
                                    top: 0,
                                    backgroundColor: styles.colors.gray50,
                                    zIndex: 2,
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                }}
                            >
                                <tr>
                                    <th style={thStyle}>Client Doc</th>
                                    <th style={thStyle}>Invoice #</th>
                                    <th style={thStyle}>Doc #</th>
                                    <th style={thStyle}>Prospect</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Bonus</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Rental</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Bank</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Fwd Bank</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Rec. Fees</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Other</th>
                                    <th style={thStyle}>Inv Date</th>
                                    <th style={thStyle}>Lessor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLeases.length === 0 ? (
                                    <tr>
                                        <td colSpan={13} style={{ padding: '3rem', textAlign: 'center', color: styles.colors.gray500 }}>
                                            {allLeases.length === 0 ? 'No leases found for this group.' : 'No leases match your filter.'}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredLeases.map((r) => {
                                        return (
                                            <tr
                                                key={r.liTg}
                                                style={{
                                                    borderBottom: `1px solid ${styles.colors.border}`,
                                                    transition: 'background-color 0.15s ease',
                                                }}
                                            >
                                                <td style={tdStyle}>{r.clientLease}</td>
                                                <td style={tdStyle}>{r.invoiceNo}</td>
                                                <td style={tdStyle}>{r.lsNo}</td>
                                                <td style={tdStyle}>
                                                    <span style={{ color: styles.colors.gray600 }}>
                                                        {r.clNo}-{r.prNo}
                                                    </span>
                                                </td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>{formatMoney(r.bonus)}</td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>{formatMoney(r.rental)}</td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>{formatMoney(r.bank)}</td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>{formatMoney(r.fwdBank)}</td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>{formatMoney(r.recordingFees)}</td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>{formatMoney(r.other)}</td>
                                                <td style={tdStyle}>{formatDate(r.invDate)}</td>
                                                <td style={tdStyle}>{r.lessor}</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Footer */}
                <div style={modalStyles.modalFooter}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span style={{ fontSize: '0.9rem', color: styles.colors.gray600 }}>Invoice Total:</span>
                        <span
                            style={{
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                color: styles.colors.gray900,
                                backgroundColor: styles.colors.gray100,
                                padding: '0.3rem 0.8rem',
                                borderRadius: '6px',
                                border: `1px solid ${styles.colors.border}`,
                            }}
                        >
                            {formatMoney(totalAmount)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Local Styles ---
const thStyle: React.CSSProperties = {
    padding: '0.75rem',
    fontWeight: 600,
    color: styles.colors.gray700,
    borderBottom: `1px solid ${styles.colors.border}`,
    textAlign: 'left',
    whiteSpace: 'nowrap',
    verticalAlign: 'bottom',
};

const tdStyle: React.CSSProperties = {
    padding: '0.6rem 0.75rem',
    color: styles.colors.gray900,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
};

const localInput: React.CSSProperties = {
    width: '100%',
    padding: '0.4rem',
    borderRadius: '4px',
    border: `1px solid ${styles.colors.gray300}`,
    fontSize: '0.9rem',
    outline: 'none',
};

export default GroupLeasesModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/GroupLeaseModal.tsx